import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserMessages, markMessageAsRead } from '../utils/messagesAPI';
import Header from '../components/Header';
import { Bell, Pen, Trophy, Mail, ChevronLeft, Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * 消息详情页面组件
 * 显示单条消息的详细内容，并标记为已读
 */
function MessageDetail({ session }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 获取消息详情
  useEffect(() => {
    const fetchMessage = async () => {
      try {
        setLoading(true);
        
        // 获取所有消息然后过滤出当前消息ID，不需要传用户ID
        const messages = await getUserMessages();
        const foundMessage = messages.find(msg => msg.id == id); // 使用==因为ID可能是数字或字符串
        
        if (foundMessage) {
          setMessage(foundMessage);
          
          // 如果消息未读，标记为已读
          if (!foundMessage.is_read) {
            await markMessageAsRead(id);
          }
        } else {
          setError('找不到消息');
        }
      } catch (error) {
        console.error('获取消息详情出错:', error.message || error);
        setError(error.message || '获取消息详情失败');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchMessage();
    }
  }, [id]); // 移除session依赖

  // 获取消息类型对应的图标
  const getIconForMessageType = (type) => {
    switch (type) {
      case 'system':
        return <Bell className="text-blue-500" />;
      case 'exam':
        return <Pen className="text-purple-500" />;
      case 'achievement':
        return <Trophy className="text-yellow-500" />;
      default:
        return <Mail className="text-gray-500" />;
    }
  };
  
  // 格式化日期时间
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // 处理动作按钮点击
  const handleAction = () => {
    if (!message || !message.link_to) return;
    
    navigate(message.link_to);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="pb-6">
      <Header 
        title="消息详情" 
        showBack={true} 
        onBack={() => navigate('/messages')}
      />
      
      {error ? (
        <div className="p-4">
          <div className="bg-red-50 p-4 rounded-lg text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
            <h3 className="text-lg font-medium text-red-800">获取消息失败</h3>
            <p className="text-red-600 mt-1">{error}</p>
            <button 
              className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg"
              onClick={() => navigate('/messages')}
            >
              返回消息列表
            </button>
          </div>
        </div>
      ) : message && (
        <div className="p-4">
          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            {/* 消息头部 */}
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mr-4">
                  {getIconForMessageType(message.type)}
                </div>
                
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-1">{message.title}</h2>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span className="mr-3">{formatDateTime(message.created_at)}</span>
                    
                    {message.is_read ? (
                      <span className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        已读
                      </span>
                    ) : (
                      <span className="flex items-center text-orange-600">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        未读
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* 消息内容 */}
            <div className="p-5">
              <div className="prose" style={{ whiteSpace: 'pre-line' }}>
                {message.content}
              </div>
              
              {message.link_to && (
                <div className="mt-6">
                  <button 
                    onClick={handleAction}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium"
                  >
                    立即查看
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessageDetail; 